import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, BookOpen, Users, Flag } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Counter } from "@/components/Counter";
import { api, LINKS } from "@/lib/api";

const TIMELINE = [
  { year: "2021", title: "Ethos is founded", body: "A small group of Bostonians comes together with a shared conviction: intentional community changes lives." },
  { year: "2022", title: "Pages of Progress begins", body: "Our flagship literacy initiative launches, putting free books directly into children's hands." },
  { year: "2023", title: "Culture takes center stage", body: "The Layers of Black History Gala and African Festival of Boston bring thousands together." },
  { year: "2024", title: "16 events, 14,370+ reached", body: "A record year of block parties, panels, and conferences across Greater Boston." },
];

const VALUES = [
  { icon: BookOpen, title: "Literacy", body: "Books open doors. We put them in every hand we can reach." },
  { icon: Heart, title: "Belonging", body: "Everyone deserves a community that sees and celebrates them." },
  { icon: Users, title: "Leadership", body: "We invest in young people so the next generation leads well." },
  { icon: Flag, title: "Representation", body: "Black heritage and diverse identities, centered and celebrated." },
];

const HERO = "https://res.cloudinary.com/dg5336ksj/image/upload/f_auto,q_auto/Arts-in-the-Park/-31.jpg";

export default function About() {
  const [stats, setStats] = useState([]);
  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page">
      <section className="bg-ink crosshatch radial-glow pt-[68px]" data-testid="about-hero">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-28 relative z-10">
          <span className="eyebrow">Our Impact</span>
          <h1 className="font-serif-eth font-light text-white mt-6 text-5xl lg:text-[72px] leading-[1.02] max-w-4xl">
            Built on purpose, <em>powered by community.</em>
          </h1>
          <p className="font-body-eth text-white/60 text-lg mt-7 max-w-2xl leading-relaxed">
            We believe vibrant, inclusive communities are the bedrock of a thriving society. Here's the story of how we got here — and the difference we've made together.
          </p>
        </div>
      </section>

      {/* Story + image */}
      <section className="bg-cream">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal className="zoom-wrap" style={{ borderRadius: 2 }}>
            <img src={HERO} alt="Ethos community" loading="lazy" className="w-full h-[480px] object-cover" style={{ borderRadius: 2 }} />
          </Reveal>
          <div>
            <Reveal><span className="eyebrow dark">Our Story</span></Reveal>
            <Reveal delay={80}>
              <h2 className="font-serif-eth font-light text-ink text-4xl lg:text-5xl mt-5 leading-tight">
                When people come together with intention, <em>extraordinary things happen.</em>
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="font-body-eth text-mute leading-relaxed mt-6">
                We started as a small group of passionate individuals who recognized the power of community events to inspire, educate, and uplift. Over the years, we've grown into a dynamic force for change — organizing events, distributing books, hosting conferences, and building bridges across cultures, generations, and backgrounds.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <p className="font-body-eth text-mute leading-relaxed mt-4">
                Every event we host, every book we give, every conversation we spark is a step toward the world we're building together.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Impact counters */}
      <section className="bg-ink2 crosshatch" data-testid="about-impact">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <Reveal><span className="eyebrow">2024 Impact</span></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif-eth font-light text-white text-4xl lg:text-5xl mt-5">Our mission <em>measured.</em></h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 100}>
                <div className="border-l border-white/15 pl-5">
                  <div className="font-serif-eth text-glow text-5xl lg:text-6xl"><Counter value={s.value} suffix={s.suffix} /></div>
                  <div className="font-body-eth text-white text-sm mt-3">{s.label}</div>
                  <div className="font-body-eth text-white/45 text-xs">{s.sub}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-sand">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Reveal><span className="eyebrow dark">What Guides Us</span></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif-eth font-light text-ink text-4xl lg:text-5xl mt-5">The values <em>we live by.</em></h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 100}>
                <div className="group eth-card bg-white border border-[var(--line)] p-7 h-full">
                  <v.icon size={26} className="text-teal" />
                  <h3 className="font-serif-eth text-2xl text-ink mt-5 underline-grow inline-block pb-1">{v.title}</h3>
                  <p className="font-body-eth text-mute text-sm leading-relaxed mt-3">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-cream">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Reveal><span className="eyebrow dark">Our Journey</span></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif-eth font-light text-ink text-4xl lg:text-5xl mt-5">From a small group to a <em>movement.</em></h2>
          </Reveal>
          <div className="mt-12 space-y-0">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 80}>
                <div className="grid grid-cols-[80px_1fr] gap-6 pb-10 border-l-2 border-[var(--line)] pl-6 relative">
                  <div className="absolute -left-[7px] top-1 w-3 h-3 bg-teal" style={{ borderRadius: 2 }} />
                  <div className="font-serif-eth text-3xl text-glow">{t.year}</div>
                  <div>
                    <h3 className="font-serif-eth text-2xl text-ink">{t.title}</h3>
                    <p className="font-body-eth text-mute text-sm leading-relaxed mt-2">{t.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink crosshatch radial-glow">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-20 text-center relative z-10">
          <h2 className="font-serif-eth font-light text-white text-4xl lg:text-5xl leading-tight">
            Be part of the <em>next chapter.</em>
          </h2>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/partnerships" className="btn-teal font-body-eth text-sm font-semibold px-8 py-3.5 inline-flex items-center gap-2">Get Involved <ArrowRight size={16} /></Link>
            <Link to="/donate" className="btn-gold font-body-eth text-sm font-semibold px-8 py-3.5">Donate Now</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
